// First-launch cinematic intro for new pilots.
//
// Mounts on the very first /play visit per device (gated by localStorage
// `cosmic_comet_intro_seen`). A comet streak sweeps across a deep-space
// background, the COSMIC WORM BREAKER title resolves out of the trail,
// then the whole overlay fades out. Auto-dismisses after ~3.2s; pilots
// can click/tap to skip. Pure CSS — no canvas, no extra deps.
import { useEffect, useState } from "react";

const STORAGE_KEY = "cosmic_comet_intro_seen";
const TOTAL_DURATION_MS = 3200;
const FADE_OUT_MS = 450;

// Read + write localStorage synchronously *during the initial render* so
// React 18 StrictMode's double-effect-invocation in dev doesn't mark the
// flag on first run and then dismiss on second. The decision is now baked
// into the component's first state — subsequent re-mounts always see it
// and short-circuit.
function checkAndMarkSeen() {
  try {
    if (localStorage.getItem(STORAGE_KEY) === "1") return false;
    localStorage.setItem(STORAGE_KEY, "1");
    return true;
  } catch {
    // Private mode or storage disabled — show the cinematic but it'll
    // replay on every visit. Acceptable degraded behavior.
    return true;
  }
}

export default function CometIntro({ onDone }) {
  const [show, setShow] = useState(checkAndMarkSeen);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!show) {
      onDone?.();
      return;
    }
    const t = setTimeout(() => setClosing(true), TOTAL_DURATION_MS - FADE_OUT_MS);
    const t2 = setTimeout(() => {
      setShow(false);
      onDone?.();
    }, TOTAL_DURATION_MS);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [show, onDone]);

  if (!show) return null;

  const skip = () => {
    setClosing(true);
    setTimeout(() => { setShow(false); onDone?.(); }, FADE_OUT_MS);
  };

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center cursor-pointer ${
        closing ? "comet-intro-fade-out" : "comet-intro-fade-in"
      }`}
      onClick={skip}
      data-testid="comet-intro"
      aria-label="Press to skip intro"
      style={{ background: "radial-gradient(ellipse at 50% 60%, #170928 0%, #05050a 70%)" }}
    >
      {/* Drifting starfield — pure CSS, zero JS cost */}
      <div className="comet-intro-stars" aria-hidden="true" />

      {/* The comet streak that sweeps across the title */}
      <div className="comet-intro-streak" aria-hidden="true" />

      {/* Title — staggered fade-in to land just as the streak passes */}
      <div className="relative z-10 text-center px-6 select-none">
        <div className="comet-intro-tagline font-mono-cosmic text-[10px] sm:text-xs tracking-[0.5em] uppercase text-[#9be4ff] mb-3">
          // BLAZING THROUGH THE STARS
        </div>
        <div className="comet-intro-title font-heading font-black uppercase text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-none">
          <span className="comet-intro-line-1">COSMIC WORM</span>
          <br />
          <span className="comet-intro-line-2">BREAKER</span>
        </div>
        <div className="comet-intro-skip font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.4em] uppercase text-white/35 mt-6">
          Tap anywhere to skip
        </div>
      </div>
    </div>
  );
}
